// How old a technician's last known fix is, as the colour of the ring drawn
// around their avatar on the Operations map.
//
// The age is measured from the fix's own timestamp (`device_position.
// device_time`), not from when the server accepted it: a fix is a place at a
// time, and a phone that uploaded two hours late still stood where the fix says
// it stood. The ramp is continuous so the map reads as a temperature rather
// than as three buckets — a ring that stepped would make a position an hour and
// a half old look identical to a brand new one, and the operator's question
// ("can I still send them there?") is answered by degree.

/** The ramp's fresh end. Zero is an anchor rather than an age any fix reaches:
 * it exists so the interpolation below spans a closed interval. */
export const RECENCY_FRESH_MS = 0;

/** One hour: where the ring is fully amber, the colour that says the position is
 * going stale but is still worth reading. */
export const RECENCY_AMBER_MS = 60 * 60 * 1000;

/** Two hours: past this the ring is grey and stays grey. A fix older than two
 * hours is not a location the office should dispatch against. */
export const RECENCY_STALE_MS = 120 * 60 * 1000;

/** The Bootstrap success / warning / secondary colours the ramp runs between. */
const FRESH_RGB = [0x19, 0x87, 0x54] as const;
const AMBER_RGB = [0xff, 0xc1, 0x07] as const;
const STALE_RGB = [0xad, 0xb5, 0xbd] as const;

const FRESH_HEX = "#198754";
const STALE_HEX = "#adb5bd";

/** The colour `t` of the way from one ramp stop to the next, `t` in 0..1,
 * interpolated in each RGB channel and printed as `#rrggbb`. Rounding keeps a
 * channel inside 0–255, so two hex digits are always enough — padded, because
 * `0x07` must not print as `7`. */
function rampColor(
	from: readonly [number, number, number],
	to: readonly [number, number, number],
	t: number,
): string {
	const channels = from.map((value, i) =>
		Math.round(value + (to[i] - value) * t)
			.toString(16)
			.padStart(2, "0"),
	);
	return `#${channels.join("")}`;
}

/**
 * The ring colour for a fix `ageMs` milliseconds old: green at `RECENCY_FRESH_MS`,
 * amber at `RECENCY_AMBER_MS`, grey from `RECENCY_STALE_MS` on, interpolated in
 * RGB between the stops. The result is always a `#rrggbb` string, so it can be
 * assigned straight to a `border-color`.
 *
 * An age that is not a number is the same absence as an ancient fix — a
 * timestamp the browser could not read places nobody — so it takes the stale
 * grey rather than a malformed colour. Both ends clamp: a clock skew that dates
 * a fix in the future reads fresh instead of extrapolating past green.
 */
export function recencyColor(ageMs: number): string {
	if (Number.isNaN(ageMs)) return STALE_HEX;
	if (ageMs <= RECENCY_FRESH_MS) return FRESH_HEX;
	if (ageMs >= RECENCY_STALE_MS) return STALE_HEX;
	if (ageMs <= RECENCY_AMBER_MS) {
		return rampColor(
			FRESH_RGB,
			AMBER_RGB,
			(ageMs - RECENCY_FRESH_MS) / (RECENCY_AMBER_MS - RECENCY_FRESH_MS),
		);
	}
	return rampColor(
		AMBER_RGB,
		STALE_RGB,
		(ageMs - RECENCY_AMBER_MS) / (RECENCY_STALE_MS - RECENCY_AMBER_MS),
	);
}
